/**
 * The Generation_Request polling hook (Feature: ai-check-generation, task 23.2; Requirement 9.2;
 * Design: §Components 11).
 *
 * Submission is asynchronous — the API's integration cap is well below the generation budget — so the
 * only way the dashboard learns what happened is by reading `GET /v1/generation-requests/{id}` until
 * the request is done. Requirement 9.2 is about that window: *while* a request is in progress there
 * must be a progress indication, which means the hook has to keep reading while the state is
 * non-terminal and stop the moment it is not.
 *
 * Three deliberate choices:
 *
 *  - **Terminality comes from {@link TERMINAL_GENERATION_REQUEST_STATES}, not from three literals
 *    spelled here.** That constant sits beside the state union in `api/types.ts`, so the hook and the
 *    page it drives cannot disagree about when generation is over, and a fourth terminal state would
 *    be a one-line change there rather than a hunt through the UI.
 *
 *  - **The next read is scheduled by a `setTimeout` chained off the previous response, not by a
 *    `setInterval`.** A fixed interval would keep firing while a slow read is still outstanding, so a
 *    request that takes longer than the interval would stack overlapping reads and could apply an
 *    older response over a newer one. Chaining also makes stopping structural: on a terminal state no
 *    further timer is created, so "polling stops at `succeeded` / `failed` / `timed_out`" is the
 *    absence of a scheduling call rather than a flag some other branch could forget to check.
 *
 *  - **The cadence is injectable** (`pollIntervalMs`), because a component test must be able to drive
 *    several polls without waiting seconds of wall clock. A test can either pass a small interval or
 *    use fake timers; either way the hook itself holds no hidden timing.
 *
 * A read that fails ends the poll and surfaces the error, exactly as {@link useResource} does for a
 * one-shot load — there is no silent retry, because a `401` or a `403` will not clear itself and a
 * caller that showed a spinner forever would be worse than one that says what went wrong. The page
 * can resubmit, which starts a fresh request and therefore a fresh poll.
 */

import { useEffect, useState } from 'react';

import { ApiError } from '../api/client';
import { useApiClient } from '../api/use-api-client';
import {
  TERMINAL_GENERATION_REQUEST_STATES,
  type CheckDraftDto,
  type GenerationRequestDto,
  type GenerationRequestState,
} from '../api/types';

/**
 * How long to wait between reads of a non-terminal Generation_Request. Generation runs for seconds to
 * a couple of minutes, so a two-second cadence is responsive without making the poll itself a load.
 */
export const DEFAULT_GENERATION_POLL_INTERVAL_MS = 2_000;

/** Whether a Generation_Request is done, per the single shared list of terminal states (Req 9.2). */
export function isTerminalGenerationState(state: GenerationRequestState): boolean {
  return TERMINAL_GENERATION_REQUEST_STATES.includes(state);
}

/**
 * What the hook reports. `idle` is the no-request-yet case — the generate page renders before anything
 * has been submitted — and is distinct from `loading` so a page does not show a spinner for a request
 * that does not exist.
 *
 * On `loaded`, `terminal` is the answer to "is generation over?", and `draft` is the Draft_Check the
 * read returned: present on `succeeded` and also on `failed` (a correction-exhausted draft is
 * persisted carrying its outstanding validation errors), never on `timed_out`, where nothing was
 * persisted at all.
 */
export type GenerationRequestPoll =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string; isAuthError: boolean }
  | {
      status: 'loaded';
      generationRequest: GenerationRequestDto;
      draft?: CheckDraftDto;
      terminal: boolean;
    };

export interface UseGenerationRequestOptions {
  /** Delay between reads of a non-terminal request; defaults to {@link DEFAULT_GENERATION_POLL_INTERVAL_MS}. */
  pollIntervalMs?: number;
}

/**
 * Poll one Generation_Request while it is non-terminal (Req 9.2).
 *
 * Pass `undefined` before anything has been submitted; the hook then sits `idle` and issues no reads.
 * Changing the id tears the previous poll down and starts a new one, so a retry cannot leave the old
 * request's timer running.
 */
export function useGenerationRequest(
  generationRequestId: string | undefined,
  options: UseGenerationRequestOptions = {},
): GenerationRequestPoll {
  const client = useApiClient();
  // Resolved to a primitive so the effect's dependency is the cadence itself, not the identity of a
  // caller-supplied options object that changes on every render.
  const pollIntervalMs = options.pollIntervalMs ?? DEFAULT_GENERATION_POLL_INTERVAL_MS;

  const [poll, setPoll] = useState<GenerationRequestPoll>(
    generationRequestId === undefined ? { status: 'idle' } : { status: 'loading' },
  );

  useEffect(() => {
    if (generationRequestId === undefined) {
      setPoll({ status: 'idle' });
      return undefined;
    }

    // `active` guards against a response from a torn-down effect clobbering a newer poll's state, the
    // same guard `useResource` uses; `timer` is what the teardown cancels.
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setPoll({ status: 'loading' });

    const read = (): void => {
      client
        .getGenerationRequest(generationRequestId)
        .then((response) => {
          if (!active) return;
          const terminal = isTerminalGenerationState(response.generationRequest.state);
          setPoll({
            status: 'loaded',
            generationRequest: response.generationRequest,
            ...(response.draft !== undefined ? { draft: response.draft } : {}),
            terminal,
          });
          // The stopping condition, expressed as not scheduling anything further.
          if (!terminal) timer = setTimeout(read, pollIntervalMs);
        })
        .catch((error: unknown) => {
          if (!active) return;
          setPoll({
            status: 'error',
            message: error instanceof Error ? error.message : 'Request failed',
            isAuthError: error instanceof ApiError && error.isAuthError,
          });
        });
    };

    read();

    return () => {
      active = false;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [client, generationRequestId, pollIntervalMs]);

  return poll;
}
